import { BarChart3, PieChart } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function AnalyticsTab({
  salesAnalytics,
  salesPeriod,
  setSalesPeriod,
  companyAnalytics,
  topCustomers,
  topProducts,
  hourlyAnalytics
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">Analíticas de Ventas</h1>
        <Select value={salesPeriod} onValueChange={setSalesPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Hoy</SelectItem>
            <SelectItem value="week">Esta Semana</SelectItem>
            <SelectItem value="month">Este Mes</SelectItem>
            <SelectItem value="year">Este Año</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sales Summary */}
      {salesAnalytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <p className="text-sm text-stone-500">Ventas Totales</p>
            <p className="text-2xl font-bold text-emerald-700">${salesAnalytics.total_sales?.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <p className="text-sm text-stone-500">Pedidos</p>
            <p className="text-2xl font-bold text-stone-900">{salesAnalytics.total_orders}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <p className="text-sm text-stone-500">Ticket Promedio</p>
            <p className="text-2xl font-bold text-stone-900">${salesAnalytics.avg_order_value?.toFixed(0)}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <p className="text-sm text-stone-500">Tendencia</p>
            <p className={`text-2xl font-bold ${salesAnalytics.growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {salesAnalytics.growth >= 0 ? '+' : ''}{salesAnalytics.growth?.toFixed(1) || 0}%
            </p>
          </div>
        </div>
      )}

      {/* Sales Chart Placeholder */}
      {salesAnalytics?.daily_data && (
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-700" />
            Ventas Diarias
          </h3>
          <div className="h-64 flex items-end gap-1">
            {salesAnalytics.daily_data.slice(-14).map((day, idx) => {
              const maxSales = Math.max(...salesAnalytics.daily_data.map(d => d.sales));
              const height = maxSales > 0 ? (day.sales / maxSales) * 100 : 0;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`$${day.sales?.toLocaleString()} - ${day.orders} pedidos`}
                  />
                  <span className="text-xs text-stone-500 transform -rotate-45 origin-left">
                    {day.date?.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sales by Company */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-emerald-700" />
            Ventas por Empresa
          </h3>
          <div className="space-y-3">
            {companyAnalytics.slice(0, 8).map((company, idx) => {
              const total = companyAnalytics.reduce((sum, c) => sum + (c.total_sales || 0), 0);
              const percentage = total > 0 ? ((company.total_sales / total) * 100).toFixed(1) : 0;
              return (
                <div key={company.company || idx}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-stone-700">{company.company}</span>
                    <span className="text-sm text-stone-500">{percentage}%</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-4">Top Clientes</h3>
          <div className="space-y-3">
            {topCustomers.slice(0, 8).map((customer, idx) => (
              <div key={customer.user_id || idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{customer.name || customer.email}</p>
                    <p className="text-xs text-stone-500">{customer.total_orders} pedidos</p>
                  </div>
                </div>
                <p className="font-bold text-emerald-700">${customer.total_spent?.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-4">Productos Más Vendidos</h3>
          <div className="space-y-3">
            {topProducts.slice(0, 8).map((product, idx) => (
              <div key={product.product_id || idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{product.name}</p>
                    <p className="text-xs text-stone-500">{product.quantity || product.quantity_sold} unidades</p>
                  </div>
                </div>
                <p className="font-bold text-blue-700">${product.revenue?.toLocaleString() || product.total_revenue?.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Distribution */}
        {hourlyAnalytics.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-semibold text-stone-900 mb-4">Distribución por Hora</h3>
            <div className="h-40 flex items-end gap-1">
              {hourlyAnalytics.map((hour, idx) => {
                const maxOrders = Math.max(...hourlyAnalytics.map(h => h.orders));
                const height = maxOrders > 0 ? (hour.orders / maxOrders) * 100 : 0;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-purple-500 rounded-t"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${hour.orders} pedidos`}
                    />
                    <span className="text-xs text-stone-500 mt-1">
                      {hour.hour}h
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
